"use client";

import { useMemo, useState } from "react";
import { BookOpen, Loader2, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Book } from "@/lib/book-types";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useAppShell } from "@/context/AppShellContext";
import {
  distinctKnowledge,
  filterBooksByKnowledge,
  selectKnowledgeScope,
} from "@/lib/knowledge-scope";
import { KnowledgeScopeSelect } from "@/components/knowledge/KnowledgeScopeSelect";

export interface BookLibraryProps {
  books: Book[];
  loading: boolean;
  error?: string | null;
  onNewBook: () => void;
  onSelectBook: (id: string) => void;
  onDeleteBook: (id: string) => Promise<void>;
}

function bookSource(book: Book, t: (key: string) => string): string {
  const knowledgeBases = book.knowledge_bases.filter(Boolean);
  if (knowledgeBases.length === 0) return t("No connected knowledge map");
  return `${knowledgeBases.join(", ")} → ${t("Knowledge map")}`;
}

export default function BookLibrary({
  books,
  loading,
  error,
  onNewBook,
  onSelectBook,
  onDeleteBook,
}: BookLibraryProps) {
  const { t } = useTranslation();
  const [deleteTarget, setDeleteTarget] = useState<Book | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await onDeleteBook(deleteTarget.id);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };
  const { activeKnowledge } = useAppShell();
  const [initialKnowledge] = useState(activeKnowledge);
  const [selectedKnowledge, setSelectedKnowledge] = useState<
    string | null | undefined
  >(undefined);
  const knowledgeOptions = useMemo(
    () => distinctKnowledge(books, (book) => book.knowledge_bases),
    [books],
  );
  const scopeKnowledge = selectKnowledgeScope(
    knowledgeOptions,
    selectedKnowledge === undefined ? initialKnowledge : selectedKnowledge,
  );
  const visibleBooks = filterBooksByKnowledge(books, scopeKnowledge);
  const hasNoScopedBooks =
    !error && !loading && scopeKnowledge !== null && visibleBooks.length === 0;
  const state = error
    ? "error"
    : loading
      ? "loading"
      : hasNoScopedBooks
        ? "filtered-empty"
        : books.length === 0
          ? "empty"
          : "ready";

  return (
    <div
      className="book-library-page h-full overflow-y-auto bg-[var(--background)]"
      data-state={state}
    >
      <div className="mx-auto max-w-[1080px] px-7 pb-[60px] pt-[22px]">
        <header className="book-library-header mb-4 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <h2 className="text-[18px] font-extrabold leading-[27.9px] text-[var(--foreground)]">
            {t("My books")}{" "}
            <span className="font-semibold text-[var(--muted-foreground)]">
              {t("Books")}
            </span>
          </h2>
          <p className="text-[13.5px] leading-[19.375px] text-[var(--muted-foreground)]">
            {t(
              "AI-generated books from knowledge maps · Documents → knowledge map → book chaining",
            )}
          </p>
          {!loading && !error ? (
            <KnowledgeScopeSelect
              options={knowledgeOptions}
              value={scopeKnowledge}
              onChange={setSelectedKnowledge}
            />
          ) : null}
        </header>

        {error ? (
          <div
            className="book-library-error rounded-[14px] border border-[var(--border)] bg-[var(--card)] px-4 py-3 text-sm text-[var(--foreground)] shadow-[var(--shadow-soft)]"
            role="alert"
          >
            <strong>{t("Error")}</strong>
            <p className="mt-1 text-[var(--muted-foreground)]">{error}</p>
          </div>
        ) : loading ? (
          <div
            aria-busy="true"
            className="book-library-loading flex min-h-[190px] items-center justify-center gap-2 text-sm text-[var(--muted-foreground)]"
          >
            <Loader2 size={16} className="animate-spin" />
            {t("Loading books…")}
          </div>
        ) : hasNoScopedBooks ? (
          <ScopedEmptyState onViewAll={() => setSelectedKnowledge(null)} />
        ) : books.length === 0 ? (
          <EmptyState onNewBook={onNewBook} />
        ) : (
          <div className="book-library-grid grid grid-cols-[repeat(auto-fill,minmax(238px,1fr))] gap-3.5">
            {visibleBooks.map((book) => (
              <div key={book.id} className="group/book-card relative min-w-0">
              <button
                type="button"
                data-book-id={book.id}
                data-book-status={book.status}
                onClick={() => onSelectBook(book.id)}
                className="book-library-card flex h-[190px] w-full min-w-0 flex-col gap-[11px] rounded-[14px] border border-[var(--border)] bg-[var(--card)] px-4 py-[15px] text-left shadow-[var(--shadow-soft)] transition-[transform,border-color] duration-150 hover:-translate-y-0.5 hover:border-[var(--ring)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]"
              >
                <span className="book-library-card__icon grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[10px] bg-[var(--muted)] text-[var(--primary)]">
                  <BookOpen size={18} strokeWidth={2} aria-hidden="true" />
                </span>
                <span
                  className="book-library-card__title line-clamp-1 text-[15px] font-bold leading-[23.25px] text-[var(--foreground)]"
                  title={book.title || t("Untitled book")}
                >
                  {book.title || t("Untitled book")}
                </span>
                <span className="book-library-card__source line-clamp-1 text-[13.5px] leading-[17.825px] text-[var(--muted-foreground)]">
                  {bookSource(book, t)}
                </span>
                <span className="book-library-card__meta text-[13.5px] leading-[18.6px] text-[var(--muted-foreground)]">
                  {t("{{count}} chapters", { count: book.chapter_count })}
                </span>
                {book.status === "error" && (
                  <span
                    className="text-[13.5px] font-bold leading-[18.6px] text-[var(--foreground)]"
                    role="alert"
                  >
                    {t("Error")}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setDeleteTarget(book)}
                aria-label={t("Delete book")}
                title={t("Delete book")}
                className="book-library-card__delete absolute right-2.5 top-2.5 z-10 grid h-7 w-7 place-items-center rounded-[8px] text-[var(--muted-foreground)] opacity-0 transition-opacity hover:bg-[var(--muted)] hover:text-red-600 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)] group-hover/book-card:opacity-100 group-focus-within/book-card:opacity-100"
              >
                <Trash2 size={15} strokeWidth={2} aria-hidden="true" />
              </button>
              </div>
            ))}

            <button
              type="button"
              onClick={onNewBook}
              className="book-library-create-card flex h-[190px] min-w-0 flex-col items-center justify-center gap-2 rounded-[14px] border border-dashed border-[var(--border)] bg-[var(--card)] p-[15px] text-center transition-[border-color,background-color] hover:border-[var(--primary)] hover:bg-[var(--muted)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]"
            >
              <span className="book-library-create-card__icon grid h-11 w-11 place-items-center rounded-[13px] bg-[var(--muted)] text-[var(--primary)]">
                <Plus size={22} strokeWidth={2} aria-hidden="true" />
              </span>
              <span className="book-library-create-card__title text-[14px] font-bold leading-[21.7px] text-[var(--foreground)]">
                {t("Create a book from a knowledge map")}
              </span>
              <span className="book-library-create-card__description text-[13.5px] leading-[17.25px] text-[var(--muted-foreground)]">
                {t("Add documents to create a knowledge map")}
                <br />
                {t("Automatically build the table of contents, pages and practice questions")}
              </span>
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title={t("Delete book")}
        confirmLabel={t("Delete")}
        tone="danger"
        busy={deleting}
        busyLabel={t("Deleting…")}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      >
        {t("“{{title}}” and all its pages will be permanently removed.", {
          title: deleteTarget?.title || t("Untitled book"),
        })}
      </ConfirmDialog>
    </div>
  );
}

function ScopedEmptyState({ onViewAll }: { onViewAll: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="book-library-empty flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-[var(--border)] bg-[var(--secondary)]/30 px-8 py-16 text-center">
      <BookOpen size={28} className="text-[var(--muted-foreground)]/50" />
      <p className="text-base font-medium text-[var(--foreground)]">
        {t("No books connected to this knowledge")}
      </p>
      <button
        type="button"
        onClick={onViewAll}
        className="inline-flex items-center gap-1.5 rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-90"
      >
        {t("View All")}
      </button>
    </div>
  );
}
function EmptyState({ onNewBook }: { onNewBook: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="book-library-empty flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-[var(--border)] bg-[var(--secondary)]/30 px-8 py-16 text-center">
      <BookOpen size={28} className="text-[var(--muted-foreground)]/50" />
      <div>
        <p className="text-base font-medium text-[var(--foreground)]">
          {t("No books yet")}
        </p>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          {t(
            "Create your first AI-generated book from a knowledge base, chat selections or simply a topic.",
          )}
        </p>
      </div>
      <button
        type="button"
        onClick={onNewBook}
        className="inline-flex items-center gap-1.5 rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-90"
      >
        <Plus size={14} />
        {t("New book")}
      </button>
    </div>
  );
}
